import React, { FC, ChangeEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    StyledDropdownToRentPrice,
    AllDropdownsWrapper,
    DropdownWrapper
} from '../../../search/common/Dropdown.styles';
import { DropdownWithLabel } from '@blueprint/dropdown';
import { MultiColumnDropdownWithLabel } from '@blueprint/multicolumn-dropdown';
import { getTranslator } from '../../i18n/getTranslator';
import {
    ToRentPriceMinSelectOptions,
    ToRentPriceMaxSelectOptions
} from '../../../search/common/SelectPriceOptions';
import searchTranslations from 'public/locales/en/search.json';
import commonTranslations from 'public/locales/en/common.json';
import {
    updateFilter,
    resetFilters,
    minPriceWeeklySelector,
    maxPriceWeeklySelector,
    minPriceMonthlySelector,
    maxPriceMonthlySelector,
    priceFrequencySelector
} from '../../../reducers/filters';
import { DropdownContainer } from '../../../search/common/DropdownContainer';
import { buildSelectPriceTitleToRent } from '../../../search/helpers';

const t = getTranslator(searchTranslations);
const tCommon = getTranslator(commonTranslations);

const priceRangeLabel = t('filters.priceRange');
const filtersFieldLabel = t('filters.field.label');
const filtersFieldPriceLabel = t('filters.field.price.label');
const minPriceLabel = t('filters.minPrice');
const maxPriceLabel = t('filters.maxPrice');
const priceFrequencyLabel = t('filters.priceFrequencyLabel');

export const SelectPriceToRent: FC = () => {
    const reduxDispatch = useDispatch();
    const {
        selectedValue: priceFrequencySelectedValue,
        options: priceFrequencyOptions
    } = useSelector(priceFrequencySelector);

    const {
        selectedValue: priceMinWeeklySelectedValue,
        options: priceMinWeeklyOptions
    } = useSelector(minPriceWeeklySelector);

    const {
        selectedValue: priceMaxWeeklySelectedValue,
        options: priceMaxWeeklyOptions
    } = useSelector(maxPriceWeeklySelector);

    const {
        selectedValue: priceMinMonthlySelectedValue,
        options: priceMinMonthlyOptions
    } = useSelector(minPriceMonthlySelector);

    const {
        selectedValue: priceMaxMonthlySelectedValue,
        options: priceMaxMonthlyOptions
    } = useSelector(maxPriceMonthlySelector);

    const priceButtonTextToRent = buildSelectPriceTitleToRent(
        priceMinWeeklySelectedValue,
        priceMaxWeeklySelectedValue,
        priceMinMonthlySelectedValue,
        priceMaxMonthlySelectedValue,
        priceFrequencySelectedValue,
        tCommon,
        t
    );

    const onPriceFrequencySelect = (evt: ChangeEvent<HTMLSelectElement>) => {
        reduxDispatch(updateFilter('priceFrequency', evt.currentTarget.value));
        if (evt.currentTarget.value === 'per_month') {
            reduxDispatch(resetFilters(['maxPriceWeekly', 'minPriceWeekly']));
        } else {
            reduxDispatch(resetFilters(['maxPriceMonthly', 'minPriceMonthly']));
        }
    };

    const onMaxMonthlyPriceChange = (evt: ChangeEvent<HTMLSelectElement>) => {
        reduxDispatch(updateFilter('maxPriceMonthly', evt.currentTarget.value));
    };

    const onMinMonthlyPriceChange = (evt: ChangeEvent<HTMLSelectElement>) => {
        reduxDispatch(updateFilter('minPriceMonthly', evt.currentTarget.value));
    };

    const onMaxWeeklyPriceChange = (evt: ChangeEvent<HTMLSelectElement>) => {
        reduxDispatch(updateFilter('maxPriceWeekly', evt.currentTarget.value));
    };

    const onMinWeeklyPriceChange = (evt: ChangeEvent<HTMLSelectElement>) => {
        reduxDispatch(updateFilter('minPriceWeekly', evt.currentTarget.value));
    };

    const priceMinMonthlyInt = parseInt(
        priceMinMonthlySelectedValue || '0',
        10
    );
    const priceMaxMonthlyInt = parseInt(
        priceMaxMonthlySelectedValue || '0',
        10
    );
    const priceMinWeeklyInt = parseInt(priceMinWeeklySelectedValue || '0', 10);
    const priceMaxWeeklyInt = parseInt(priceMaxWeeklySelectedValue || '0', 10);

    return (
        <MultiColumnDropdownWithLabel
            title={priceButtonTextToRent}
            label={priceRangeLabel}
            testId="AnyPrice_testId"
            isLeft
        >
            <AllDropdownsWrapper>
                <DropdownContainer>
                    <DropdownWithLabel
                        id="desktop_price_frequency-filter"
                        data-testid="desktop_price_frequency-filter-select"
                        onChange={onPriceFrequencySelect}
                        value={priceFrequencySelectedValue}
                        name="priceFrequency"
                        hasBorderRadius
                        aria-label="Price Frequency"
                        label={priceFrequencyLabel}
                    >
                        {' '}
                        {priceFrequencyOptions ? (
                            priceFrequencyOptions?.map((option) => (
                                <option
                                    key={option?.value}
                                    value={option?.value}
                                >
                                    {option.label}
                                </option>
                            ))
                        ) : (
                            <option>{filtersFieldLabel}</option>
                        )}
                    </DropdownWithLabel>
                </DropdownContainer>
                {priceFrequencySelectedValue !== 'per_week' && (
                    <DropdownWrapper>
                        <DropdownContainer>
                            <StyledDropdownToRentPrice
                                onChange={onMinMonthlyPriceChange}
                                value={priceMinMonthlySelectedValue}
                                name="price_min_monthly"
                                id="price_min_monthly"
                                hasBorderRadius
                                aria-label="Min Price monthly"
                                data-testid="min_price_monthly_testid"
                                label={minPriceLabel}
                            >
                                {priceMinMonthlyOptions ? (
                                    <ToRentPriceMinSelectOptions
                                        options={priceMinMonthlyOptions}
                                        priceOtherInt={priceMaxMonthlyInt}
                                        keyPrefix="minmonth"
                                    />
                                ) : (
                                    <option>{filtersFieldPriceLabel}</option>
                                )}
                            </StyledDropdownToRentPrice>
                        </DropdownContainer>
                        <DropdownContainer>
                            <StyledDropdownToRentPrice
                                onChange={onMaxMonthlyPriceChange}
                                value={priceMaxMonthlySelectedValue}
                                name="price_max_monthly"
                                id="price_max_monthly"
                                hasBorderRadius
                                aria-label="Max Price monthly"
                                data-testid="max_price_monthly_testid"
                                label={maxPriceLabel}
                            >
                                {priceMaxMonthlyOptions ? (
                                    <ToRentPriceMaxSelectOptions
                                        options={priceMaxMonthlyOptions}
                                        keyPrefix="maxmonth"
                                        priceOtherInt={priceMinMonthlyInt}
                                    />
                                ) : (
                                    <option>{filtersFieldPriceLabel}</option>
                                )}
                            </StyledDropdownToRentPrice>
                        </DropdownContainer>
                    </DropdownWrapper>
                )}
                {priceFrequencySelectedValue == 'per_week' && (
                    <DropdownWrapper>
                        <DropdownContainer>
                            <StyledDropdownToRentPrice
                                onChange={onMinWeeklyPriceChange}
                                value={priceMinWeeklySelectedValue}
                                name="price_min_weekly"
                                id="price_min_weekly"
                                hasBorderRadius
                                aria-label="Min Price weekly"
                                data-testid="min_price_weekly_testid"
                                label={minPriceLabel}
                            >
                                {priceMinWeeklyOptions ? (
                                    <ToRentPriceMinSelectOptions
                                        options={priceMinWeeklyOptions}
                                        priceOtherInt={priceMaxWeeklyInt}
                                        keyPrefix="minweek"
                                    />
                                ) : (
                                    <option>{filtersFieldPriceLabel}</option>
                                )}
                            </StyledDropdownToRentPrice>
                        </DropdownContainer>
                        <DropdownContainer>
                            <StyledDropdownToRentPrice
                                onChange={onMaxWeeklyPriceChange}
                                value={priceMaxWeeklySelectedValue}
                                name="price_max_weekly"
                                id="price_max_weekly"
                                hasBorderRadius
                                aria-label="Max Price weekly"
                                data-testid="max_price_weekly_testid"
                                label={maxPriceLabel}
                            >
                                {priceMaxWeeklyOptions ? (
                                    <ToRentPriceMaxSelectOptions
                                        options={priceMaxWeeklyOptions}
                                        keyPrefix="maxweek"
                                        priceOtherInt={priceMinWeeklyInt}
                                    />
                                ) : (
                                    <option>{filtersFieldPriceLabel}</option>
                                )}
                            </StyledDropdownToRentPrice>
                        </DropdownContainer>
                    </DropdownWrapper>
                )}
            </AllDropdownsWrapper>
        </MultiColumnDropdownWithLabel>
    );
};
